export type ValidationResult = {
  valid: boolean;
  errors: string[];
};

/**
 * validateFilteringDefinition
 * Expects `obj` to be the filtering_definition object (as your component currently returns).
 *
 * Changed rule:
 * - for non-activity filters, `value` must be a non-empty array (length > 0)
 */
export function validateFilteringDefinition(obj: any): ValidationResult {
  const errors: string[] = [];

  if (!obj || typeof obj !== "object") {
    errors.push("Root must be an object representing filtering_definition");
    return { valid: false, errors };
  }

  const filtering = obj; // root is the filtering_definition itself

  // helper to validate a single activity entry
  const validateActivityEntry = (a: any, tag: string) => {
    if (!a || typeof a !== "object") {
      errors.push(`${tag} must be an object`);
      return;
    }
    if (!a.field || typeof a.field !== "string" || a.field.trim() === "") {
      errors.push(`${tag}.field is required and must be a non-empty string`);
    }
    if (!Array.isArray(a.raw_table_format) || a.raw_table_format.length === 0) {
      errors.push(`${tag}.raw_table_format must be a non-empty array`);
    } else {
      let hasPrimaryRawFormat = false;
      a.raw_table_format.forEach((fmt: any, idx: number) => {
        if (!fmt || typeof fmt !== "object") {
          errors.push(`${tag}.raw_table_format[${idx}] must be an object`);
          return;
        }
        if (
          fmt.raw_table_format &&
          typeof fmt.raw_table_format === "string" &&
          fmt.raw_table_format.trim() !== ""
        ) {
          hasPrimaryRawFormat = true;
        }
        // optional field validation (only if present)
        if (
          fmt.additional_raw_table_format !== undefined &&
          (typeof fmt.additional_raw_table_format !== "string" ||
            fmt.additional_raw_table_format.trim() === "")
        ) {
          errors.push(
            `${tag}.raw_table_format[${idx}].additional_raw_table_format must be a non-empty string when provided`,
          );
        }
      });
      if (!hasPrimaryRawFormat) {
        errors.push(
          `${tag}.raw_table_format must contain at least one raw_table_format entry`,
        );
      }
    }
  };

  // Validate activity_date exists and is either an object or a non-empty array
  const activity = filtering["activity_date"];
  if (activity === undefined || activity === null) {
    errors.push("activity_date is required");
  } else if (Array.isArray(activity)) {
    if (activity.length === 0) {
      errors.push(
        "activity_date must be a non-empty array when an array is provided",
      );
    } else {
      activity.forEach((a: any, idx: number) =>
        validateActivityEntry(a, `activity_date[${idx}]`),
      );
    }
  } else if (typeof activity === "object") {
    // single object allowed
    validateActivityEntry(activity, "activity_date");
  } else {
    errors.push(
      "activity_date must be either an object or an array of objects",
    );
  }

  // Validate other keys at root
  for (const key of Object.keys(filtering)) {
    if (typeof key !== "string" || key.trim() === "") {
      errors.push("filtering_definition contains an empty key");
      continue;
    }
    if (key === "activity_date") continue; // already validated

    const val = filtering[key];
    if (!val || typeof val !== "object") {
      errors.push(`filter ${key} must be an object`);
      continue;
    }

    // NOTE: changed rule — value must be a non-empty array
    if (!Array.isArray(val.value) || val.value.length === 0) {
      errors.push(`filter ${key}.value must be a non-empty array`);
    }

    // condition must be a non-empty string
    if (
      !val.condition ||
      typeof val.condition !== "string" ||
      val.condition.trim() === ""
    ) {
      errors.push(
        `filter ${key}.condition is required and must be a non-empty string`,
      );
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * React-style hook (lightweight) to derive validation from a preview object.
 * Expects previewObject to be the filtering_definition itself.
 */
export function useFilterObjValidation(previewObject: any): ValidationResult {
  return validateFilteringDefinition(previewObject);
}
